import { DefaultOptions, QueryClient } from "@tanstack/react-query";
import { logger } from "./logger";

/**
 * 🚀 PROFESSIONAL QUERY CLIENT CONFIGURATION
 *
 * Configured to match industry standards from Todoist, Linear, and Asana:
 * - Aggressive caching with smart invalidation
 * - Optimistic updates with rollback
 * - Background refetching for fresh data
 * - Offline support with retry logic
 */
export const createQueryClientDefaultOptions = (): DefaultOptions => ({
  queries: {
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    refetchOnMount: false,
    retry: (failureCount, error: unknown) => {
      const errorWithStatus = error as { status?: number };
      if (
        errorWithStatus?.status &&
        errorWithStatus.status >= 400 &&
        errorWithStatus.status < 500
      ) {
        return false;
      }
      return failureCount < 3;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  },
  mutations: {
    retry: 1,
    onError: (error, variables) => {
      logger.error("Mutation failed:", { error, variables });
    },
  },
});

export const queryClient = new QueryClient({
  defaultOptions: createQueryClientDefaultOptions(),
});

/**
 * 🎯 GENERIC QUERY KEY FACTORY
 *
 * Helper function to create consistent query keys for any entity type
 */
export const createQueryKeys = <T extends string>(entityType: T) => ({
  all: [entityType] as const,
  lists: () => [entityType, "list"] as const,
  list: (filters: Record<string, unknown>) =>
    [entityType, "list", filters] as const,
  details: () => [entityType, "detail"] as const,
  detail: (id: string) => [entityType, "detail", id] as const,
  search: (query: string) => [entityType, "search", query] as const,
});

/**
 * 🎯 GENERIC INVALIDATION HELPERS
 *
 * Helper functions to invalidate queries for any entity type
 */
export const createInvalidateHelpers = <T extends string>(entityType: T) => {
  const keys = createQueryKeys(entityType);

  return {
    all: () => queryClient.invalidateQueries({ queryKey: keys.all }),
    lists: () => queryClient.invalidateQueries({ queryKey: keys.lists() }),
    detail: (id: string) =>
      queryClient.invalidateQueries({ queryKey: keys.detail(id) }),
    search: (query: string) =>
      queryClient.invalidateQueries({ queryKey: keys.search(query) }),
  };
};

/**
 * 🎯 GENERIC OPTIMISTIC UPDATE HELPERS
 *
 * Helper functions for optimistic updates of any entity type
 */
export const createOptimisticHelpers = <
  T extends { id: string; updated_at?: string },
>(
  entityType: string
) => {
  const keys = createQueryKeys(entityType);

  return {
    /**
     * Update entity in cache optimistically with immediate visual feedback
     */
    updateEntity: (entityId: string, updates: Partial<T>) => {
      // Update all entity list queries
      queryClient.setQueriesData(
        { queryKey: keys.lists() },
        (oldData: T[] | undefined) => {
          if (!oldData) return oldData;
          return oldData.map((entity: T) =>
            entity.id === entityId
              ? {
                  ...entity,
                  ...updates,
                  updated_at: new Date().toISOString(), // Ensure timestamp updates
                }
              : entity
          );
        }
      );

      // Also update individual entity cache if it exists
      queryClient.setQueryData(
        keys.detail(entityId),
        (oldEntity: T | undefined) => {
          if (!oldEntity) return oldEntity;
          return {
            ...oldEntity,
            ...updates,
            updated_at: new Date().toISOString(),
          };
        }
      );

      // 🚀 FORCE IMMEDIATE RE-RENDER: Invalidate to trigger component updates
      queryClient.invalidateQueries({
        queryKey: keys.lists(),
        refetchType: "none", // Don't refetch, just trigger re-render
      });
    },

    /**
     * Add new entity to cache optimistically
     */
    addEntity: (newEntity: T) => {
      queryClient.setQueriesData(
        { queryKey: keys.lists() },
        (oldData: T[] | undefined) => {
          if (!oldData) return [newEntity];
          return [newEntity, ...oldData];
        }
      );
    },

    /**
     * Remove entity from cache optimistically
     */
    removeEntity: (entityId: string) => {
      queryClient.setQueriesData(
        { queryKey: keys.lists() },
        (oldData: T[] | undefined) => {
          if (!oldData) return oldData;
          return oldData.filter((entity: T) => entity.id !== entityId);
        }
      );
    },
  };
};

/**
 * 🎯 CACHE UTILITIES
 *
 * Utilities for managing cache state
 */
export const cacheUtils = {
  /**
   * Clear all cached data
   */
  clearAll: () => queryClient.clear(),

  /**
   * Get cached data for a specific query key
   */
  getCachedData: <T>(queryKey: readonly unknown[]) =>
    queryClient.getQueryData<T>(queryKey),

  /**
   * Set cached data for a specific query key
   */
  setCachedData: <T>(queryKey: readonly unknown[], data: T) =>
    queryClient.setQueryData(queryKey, data),

  /**
   * Remove specific cached data
   */
  removeCache: (queryKey: readonly unknown[]) =>
    queryClient.removeQueries({ queryKey }),
};
